import { useSelector } from "react-redux";

// useAuthStatus (SCO-1819) — the ONE shared "who is logged in?" resolver for
// the home surfaces. Player and organizer auth are fully independent systems
// (docs/architecture/03-auth.md): player = userID+pin (playerSlice +
// localStorage["player"], no Firebase involved), organizer = Firebase bearer
// token (organizerSlice). READ-ONLY — never dispatches (setPlayer merges
// truthy-only, so "logging out" through it is impossible anyway).
//
// Player identity resolution is PRESENCE-precedence, not `||`:
// 1. If the URL declares the identity group (`?user=` present, EVEN EMPTY),
//    the URL wins for user/pin/account. The native shells declare logged-OUT
//    by loading the hero with `?user=&pin=`, and webview localStorage
//    persists across reloads (shared default WKWebsiteDataStore;
//    window.logoutExternal only exists while MainProfile is mounted) — a
//    `param || localStorage` fallback would visually resurrect a stale
//    localStorage["player"] after a native logout. Same pattern as
//    MainProfile's `params.has("user")` userid init.
// 2. Else Redux state.player (populated by setPlayer on web login).
// 3. Else localStorage["player"] (guarded parse).

export function readStoredPlayer() {
  try {
    return JSON.parse(localStorage.getItem("player")) || {};
  } catch (e) {
    return {};
  }
}

// Pure resolver — exported for tests (no store or Router needed).
export function resolveAuthStatus({
  params,
  player = {},
  organizer = {},
  stored = {},
}) {
  const urlDeclared = params.has("user");
  const playerId =
    (urlDeclared ? params.get("user") : player.userID || stored.userID) || "";
  const pin =
    (urlDeclared ? params.get("pin") : player.pin || stored.pin) || "";
  const accountId =
    (urlDeclared
      ? params.get("account")
      : player.accountID || stored.accountID) || "";
  const platform = params.get("platform");
  return {
    // Mirrors native's logged-in check: defaultuser != "" && defaultpin != "".
    playerLoggedIn: !!(playerId && pin),
    playerId,
    pin,
    accountId,
    organizerLoggedIn: !!organizer.bearerToken && !organizer.isLogout,
    organizerAccountId: organizer.accountid || "",
    platform,
    inApp: !!platform,
  };
}

export default function useAuthStatus() {
  const player = useSelector((state) => state.player);
  const organizer = useSelector((state) => state.organizer);
  const params = new URLSearchParams(window.location.search);
  return resolveAuthStatus({
    params,
    player,
    organizer,
    stored: readStoredPlayer(),
  });
}
